import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { BRAND_NAME } from '../config/brand';
import { AuthShell } from '../components/dashboard/AuthShell';
import { DashRoot } from '../components/dashboard/primitives';

export const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selfHostedCode, setSelfHostedCode] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Check existing cookie auth — no escalation, OTP-only
  useEffect(() => {
    setCheckingAuth(true);
    fetch(`${API_BASE_URL}/api/admin/dashboard`, { credentials: 'include' })
      .then(res => {
        if (res.ok) navigate('/admin/verifications');
        else setCheckingAuth(false);
      })
      .catch(() => setCheckingAuth(false));
  }, [navigate]);

  // Auto-focus OTP input when step changes
  useEffect(() => {
    if (step === 'otp') otpInputRef.current?.focus();
  }, [step]);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSelfHostedCode(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reviewer/otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Failed to send verification code');
        return;
      }

      // Self-hosted: show code inline
      if (data.self_hosted && data.code) {
        setSelfHostedCode(data.code);
      }

      setStep('otp');
    } catch (err: any) {
      setError(err.message ?? 'Could not reach the server');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/reviewer/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, code: otpCode }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Invalid verification code');
        return;
      }

      if (!data.token) {
        setError('Login failed: invalid response from server');
        return;
      }

      // Token is now set as httpOnly cookie by the server
      navigate('/admin/verifications');
    } catch (err: any) {
      setError(err.message ?? 'Could not reach the server');
    } finally {
      setLoading(false);
    }
  };

  // Spinner while checking existing auth
  if (checkingAuth) {
    return (
      <DashRoot>
        <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="sf-skel" style={{ width: 56, height: 56, borderRadius: 999, margin: '0 auto 16px' }} />
            <p style={{ color: 'var(--sf-mid)', fontSize: 14 }}>Authenticating…</p>
          </div>
        </div>
      </DashRoot>
    );
  }

  return (
    <AuthShell
      eyebrow="Review dashboard"
      headline={<>Review every identity with confidence.</>}
      blurb={`Approve, reject and override verifications for your organisation — signed in with a one-time code, no passwords. ${BRAND_NAME} keeps every decision on the audit trail.`}
    >
      <h1>{step === 'email' ? 'Sign in' : 'Check your email'}</h1>
      <p className="sf-sub">
        {step === 'email'
          ? 'Enter your reviewer email and we’ll send you a 6-digit code.'
          : <>Enter the code sent to <b style={{ color: 'var(--sf-ink)' }}>{email}</b></>}
      </p>

      {error && <div className="sf-alert" role="alert">{error}</div>}

      {step === 'email' ? (
        <form onSubmit={handleSendOtp}>
          <label className="sf-label" htmlFor="admin-email">Email address</label>
          <input
            id="admin-email"
            className="sf-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="reviewer@company.com"
            required
            autoFocus
          />
          <button type="submit" disabled={loading} className="sf-pill primary lg block" style={{ marginTop: 20 }}>
            {loading ? 'Sending code…' : 'Send verification code'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleVerifyOtp}>
          {selfHostedCode && (
            <div className="sf-card muted" style={{ padding: 16, borderRadius: 20, textAlign: 'center', marginBottom: 18 }}>
              <div style={{ color: 'var(--sf-mid)', fontSize: 12.5, marginBottom: 4 }}>Self-hosted mode — your code</div>
              <div style={{ color: 'var(--sf-accent)', fontSize: 28, fontWeight: 600, letterSpacing: '0.18em' }} className="sf-num">{selfHostedCode}</div>
            </div>
          )}
          <label className="sf-label" htmlFor="admin-otp">Verification code</label>
          <input
            id="admin-otp"
            ref={otpInputRef}
            className="sf-input sf-num"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            required
            style={{ height: 60, textAlign: 'center', fontSize: 26, letterSpacing: '0.3em', fontWeight: 600 }}
          />
          <button type="submit" disabled={loading || otpCode.length !== 6} className="sf-pill primary lg block" style={{ marginTop: 20 }}>
            {loading ? 'Verifying…' : 'Verify & sign in'}
          </button>
          <button
            type="button"
            onClick={() => { setStep('email'); setOtpCode(''); setError(''); setSelfHostedCode(null); }}
            className="sf-pill grey lg block"
            style={{ marginTop: 10 }}
          >
            Use a different email
          </button>
        </form>
      )}

      <div style={{ textAlign: 'center', marginTop: 26 }}>
        <a href="/" style={{ color: 'var(--sf-mid)', fontSize: 13.5 }}>&larr; Back to developer portal</a>
      </div>
    </AuthShell>
  );
};
